// isAdmin.ts — v70.036. Single place that decides whether the current user is staff.
//
// Extracted because the same JWT-role check was being written inline per component
// (ReviewComposer had its own copy), which is how a gate ends up applied in one place and
// forgotten in another — exactly what happened with the live vendor layer, whose own file
// header said "Admin-gated for now via the parent's `visible`" while the parent passed a
// hardcoded `true`.
//
// Reads the role out of the JWT rather than calling the API so it is synchronous and usable
// directly in render. This is a UI GATE ONLY — it decides what to show, never what is
// allowed. Every endpoint behind these controls enforces its own auth server-side; a user who
// forges a token client-side just sees buttons that then fail.

export function hasAdminRole(): boolean {
  // 🔴 v70.038 — CHECKS BOTH SIGNALS ON PURPOSE.
  //
  // Two different admin checks already existed side by side: this JWT-role one, and
  // BottomNav's isAdminUser() which read the `hl_user` object / `user_role` string from
  // localStorage. They can DISAGREE — and when they do you get the worst possible outcome:
  // the 🛵 Live TAB visible but its vendor layer gated off, i.e. a tab that opens an empty
  // map. That is exactly what Patrick hit on staging ("I cannot see any drivers at all nor
  // trail or nothing"). One helper, both signals, so tab and layer can never disagree.
  try {
    const u = JSON.parse(localStorage.getItem('hl_user') || 'null');
    if (String(u?.role || '').toLowerCase() === 'admin') return true;
  } catch { /* unparseable → fall through to the JWT check */ }
  if ((localStorage.getItem('user_role') || '').toLowerCase() === 'admin') return true;
  try {
    const token =
      localStorage.getItem('hl_auth_token') || localStorage.getItem('token') || '';
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const role = String(payload?.role ?? '').toUpperCase();
    return (
      role === 'ADMIN' ||
      role === 'MANAGER' ||
      payload?.isAdmin === true ||
      (Array.isArray(payload?.roles) &&
        payload.roles.some((r: string) => ['admin', 'manager'].includes(String(r).toLowerCase())))
    );
  } catch {
    return false;
  }
}
